// The tenant the user last picked in the workspace switcher, remembered across a
// page reload (#364 F9b). Before this, a reload always fell back to the first
// membership the server happened to return, so a multi-tenant admin lost their
// selection on every refresh.
//
// OWNER-STAMPED, exactly like the shared `activeRoleId:{slug}` /
// `activeRoleOwner:{slug}` pair in @lumi/app-kit (AGENTS.md, #317 A70): the
// workspace and the module UIs share one browser, and a value written for one
// account must never be honoured for the next one signed into it. The stamp
// records WHO chose WHAT, so a value whose stamp does not vouch for both is
// refused — fail closed to the default tenant.
//
// The stored id is never trusted on its own either: the caller (the tenants
// store) only honours it when the freshly fetched membership list still contains
// it, so a tenant the user was removed from cannot come back through storage.
const TENANT_KEY = 'kb_workspace_tenant';
const TENANT_OWNER_KEY = 'kb_workspace_tenantOwner';

/** Both keys, for the sign-out clear in the auth store. */
export const PICKED_TENANT_STORAGE_KEYS: readonly string[] = [TENANT_KEY, TENANT_OWNER_KEY];

// userIds are UUIDs and tenant ids are UUIDs/cuids — neither contains `|`.
const STAMP_SEPARATOR = '|';

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * The tenant id this user picked, or null when there is none we can vouch for
 * (no session, no stamp, another account's stamp, or a stamp that no longer
 * describes what is stored).
 */
export function readPickedTenantId(userId: string | null | undefined): string | null {
  const value = safeGet(TENANT_KEY);
  if (!value) return null;
  // Without a signed-in user there is nobody to attribute the choice to.
  if (!userId) return null;
  const stamped = safeGet(TENANT_OWNER_KEY);
  if (!stamped) return null;

  const sep = stamped.indexOf(STAMP_SEPARATOR);
  const stampedBy = sep === -1 ? null : stamped.slice(0, sep);
  const stampedValue = sep === -1 ? null : stamped.slice(sep + 1);

  if (stampedBy === userId && stampedValue === value) return value;

  // Our own write, for the account that used this browser before: drop both, so
  // the previous user's workspace is not what this one lands in.
  if (stampedValue === value) {
    safeRemove(TENANT_KEY);
    safeRemove(TENANT_OWNER_KEY);
  }
  return null;
}

/** Remember this user's pick. Silently does nothing without both ids. */
export function writePickedTenantId(userId: string | null | undefined, tenantId: string | null | undefined): void {
  if (!userId || !tenantId) return;
  safeSet(TENANT_KEY, tenantId);
  safeSet(TENANT_OWNER_KEY, `${userId}${STAMP_SEPARATOR}${tenantId}`);
}

export function clearPickedTenantId(): void {
  safeRemove(TENANT_KEY);
  safeRemove(TENANT_OWNER_KEY);
}
